import { PageLayout } from '~/shared/PageLayout';
import { Calendar, PieChart, Timeline } from 'lucide-react';
import { Section } from '~/shared/section/Section';
import { DataTableFilterInjectContextProvider } from '~/shared/data-tables/query/DataTableFilterInjectContext';
import { QueryDataTableFilterBar } from '~/shared/data-tables/query/QueryDataTableFilterBar';
import { FilterBar } from '~/shared/components/filter-bar/FilterBar';
import { LegacyOasisTrackingTable } from './LegacyOasisTrackingTable';
import { useQueryDataTableFilter } from '~/shared/data-tables/query/useQueryDataTableFilter';
import { AutocompleteSearchField } from '~/shared/forms/controls/base-fields/AutocompleteSearchField';
import { SelectField } from '~/shared/forms/controls/base-fields/SelectField';
import { useSuspenseQuery } from '@tanstack/react-query';
import { getLegacyOasisFormTrackingTypesOptions, type LegacyOasisTrackingFormTypeViewModel } from '@generated';
import { QueryParamTabs } from '~/shared/components/tabs/QueryParamTabs';
import { Tabs } from '~/shared/components/tabs/Tabs';
import { LegacyOasisTrackingAveragesTable } from './LegacyOasisTrackingAveragesTable';

const tabs = [
	{ id: 'list', label: 'List', icon: Calendar },
	{ id: 'averages', label: 'Averages', icon: PieChart }
];

export function LegacyOasisTracking() {
	const { data: formTypes } = useSuspenseQuery(getLegacyOasisFormTrackingTypesOptions());
	return (
		<PageLayout pageHeader={{ title: 'OASIS Tracking', description: 'Track OASIS form submissions.', TitleIcon: Timeline }}>
			<QueryParamTabs defaultTab={tabs[0].id} paramName="selectedTab" clearRemainingParams>
				<Tabs.TabList aria-label="OASIS Tracking Sections">
					{tabs.map(tab => (
						<Tabs.Tab key={tab.id} id={tab.id}>
							<div className="flex items-center gap-2">
								<tab.icon size={18} />
								{tab.label}
							</div>
						</Tabs.Tab>
					))}
				</Tabs.TabList>
				<Tabs.TabPanel id="list">
					<Section>
						<DataTableFilterInjectContextProvider>
							<div className="space-y-4">
								<QueryDataTableFilterBar>
									<FilterBar.Item label="User">
										<OasisTrackingUser />
									</FilterBar.Item>
									<FilterBar.Item label="Form Type">
										<OasisTrackingFormTypeFilter formTypes={formTypes} />
									</FilterBar.Item>
									<FilterBar.Item label="Patient">
										<OasisPatientFilter />
									</FilterBar.Item>
								</QueryDataTableFilterBar>
								<LegacyOasisTrackingTable />
							</div>
						</DataTableFilterInjectContextProvider>
					</Section>
				</Tabs.TabPanel>
				<Tabs.TabPanel id="averages">
					<DataTableFilterInjectContextProvider>
						<div className="space-y-4">
							<QueryDataTableFilterBar>
								<FilterBar.Item label="User">
									<OasisTrackingUser />
								</FilterBar.Item>
							</QueryDataTableFilterBar>
							<LegacyOasisTrackingAveragesTable />
						</div>
					</DataTableFilterInjectContextProvider>
				</Tabs.TabPanel>
			</QueryParamTabs>
		</PageLayout>
	);
}

export function OasisTrackingUser() {
	const [caregiverCode, setCaregiverCode] = useQueryDataTableFilter({
		name: 'caregiverCode',
		convertFromQueryParam: value => value.toString()
	});
	return (
		<AutocompleteSearchField
			label="User"
			hideLabel
			placeholder="All Users"
			searchType="legacy-user"
			className="min-w-[350px]"
			value={caregiverCode}
			onChange={id => setCaregiverCode(id)}
		/>
	);
}

function OasisPatientFilter() {
	const [patient, setPatient] = useQueryDataTableFilter({
		name: 'patientCode',
		convertFromQueryParam: value => value.toString()
	});
	return (
		<AutocompleteSearchField
			label="Patient"
			hideLabel
			placeholder="All Patients"
			searchType="legacy-patients"
			className="min-w-[350px]"
			value={patient}
			onChange={id => setPatient(id)}
		/>
	);
}

function OasisTrackingFormTypeFilter({ formTypes }: { formTypes: LegacyOasisTrackingFormTypeViewModel[] }) {
	const [formTypeId, setFormTypeId] = useQueryDataTableFilter({
		name: 'oasisTrackingFormTypeId',
		convertFromQueryParam: value => value.toString()
	});
	return (
		<SelectField
			label="Form Type"
			hideLabel
			placeholder="All Form Types"
			value={formTypeId}
			onChange={id => setFormTypeId(id)}
			items={formTypes.map(x => ({ id: x.id.toString(), name: x.name }))}
		/>
	);
}
